import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "@emotion/styled";
import { Photo } from "@models/photo";
import { useTranslation } from "react-i18next";
import { Box, Grid2 as Grid, TextField } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { DateUtil } from "@utils/date.util";

const Label = styled.div`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  color: #6f7787ff;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Value = styled.div`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  color: #171a1fff;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export interface PhotoMetaDataSavePayload {
  placeId?: string;
  userDescription?: string;
}

export interface PhotoMetaDataProps {
  photo: Photo;
  isEditMode?: boolean;
  onSave?: (payload: PhotoMetaDataSavePayload) => void;
}

export interface PhotoMetaDataHandle {
  save: () => void;
}

const PhotoMetaData = forwardRef<PhotoMetaDataHandle, PhotoMetaDataProps>(
  function PhotoMetaData({ photo, isEditMode = false, onSave }, ref) {
  const { coordinates, place, photoDate, createdDate, userDescription } = photo;
  const { t } = useTranslation();
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });
  const [placesLibrary, setPlacesLibrary] = useState<{
    PlaceAutocompleteElement: new () => HTMLElement;
  } | null>(null);
  const [photoLocation, setPhotoLocation] = useState<
    { lat: number; lng: number } | undefined
  >();
  const [editPlaceId, setEditPlaceId] = useState<string | undefined>(
    place?.id ?? undefined,
  );
  const [editLocationDisplay, setEditLocationDisplay] = useState(
    place?.name ?? "",
  );
  const [editUserDescription, setEditUserDescription] = useState(
    userDescription ?? "",
  );
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [savedLocationForCancel, setSavedLocationForCancel] = useState<{
    placeId: string | undefined;
    display: string;
  }>({ placeId: undefined, display: "" });
  const [selectedPlaceLocation, setSelectedPlaceLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const autocompleteContainerRef = useRef<HTMLDivElement>(null);
  const autocompleteElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setEditPlaceId(place?.id ?? undefined);
    setEditLocationDisplay(place?.name ?? "");
    setEditUserDescription(userDescription ?? "");
    setIsEditingLocation(false);
    setSelectedPlaceLocation(null);
  }, [photo.id, place?.id, place?.name, userDescription]);

  useEffect(() => {
    if (!isEditMode || !isLoaded || !window.google?.maps?.importLibrary) return;
    let cancelled = false;
    (async () => {
      try {
        const lib = await window.google.maps.importLibrary("places");
        const { PlaceAutocompleteElement } = lib as unknown as { PlaceAutocompleteElement: new () => HTMLElement };
        if (cancelled) return;
        setPlacesLibrary({ PlaceAutocompleteElement });
      } catch {
        if (!cancelled) setPlacesLibrary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, isLoaded]);

  const showLocationField = isEditingLocation || !editLocationDisplay;
  useEffect(() => {
    if (!isEditMode || !placesLibrary || !autocompleteContainerRef.current || !showLocationField) return;
    const container = autocompleteContainerRef.current;
    if (container.children.length > 0) return;
    const PlaceAutocompleteElement = placesLibrary.PlaceAutocompleteElement;
    const el = new PlaceAutocompleteElement() as HTMLElement;
    el.id = "photo-place-autocomplete";
    const handleSelect = async (e: Event & { placePrediction?: { toPlace: () => Promise<{ fetchFields: (opts: { fields: string[] }) => Promise<void>; id?: string; formattedAddress?: string; location?: { lat: () => number; lng: () => number } }> } }) => {
      try {
        const placePrediction = e.placePrediction;
        if (!placePrediction) return;
        const place = await placePrediction.toPlace();
        await place.fetchFields({ fields: ["id", "formattedAddress", "location"] });
        const id = (place as { id?: string }).id;
        const formattedAddress = (place as { formattedAddress?: string }).formattedAddress;
        const location = (place as {
          location?:
            | { lat: () => number; lng: () => number }
            | { lat: number; lng: number };
        }).location;
        if (id) {
          setEditPlaceId(id);
          setEditLocationDisplay(formattedAddress ?? "");
          if (location) {
            const lat =
              typeof location.lat === "function"
                ? location.lat()
                : (location as { lat: number }).lat;
            const lng =
              typeof location.lng === "function"
                ? location.lng()
                : (location as { lng: number }).lng;
            if (typeof lat === "number" && typeof lng === "number") {
              setSelectedPlaceLocation({ lat, lng });
            } else {
              setSelectedPlaceLocation(null);
            }
          } else {
            setSelectedPlaceLocation(null);
          }
          setIsEditingLocation(false);
        }
      } catch {
        // ignore
      }
    };
    el.addEventListener("gmp-select", handleSelect as EventListener);
    container.appendChild(el);
    autocompleteElementRef.current = el;
    return () => {
      el.removeEventListener("gmp-select", handleSelect as EventListener);
      if (container.contains(el)) container.removeChild(el);
      autocompleteElementRef.current = null;
    };
  }, [isEditMode, placesLibrary, showLocationField]);

  const mapCenter =
    selectedPlaceLocation ??
    (coordinates
      ? { lat: coordinates.latitude, lng: coordinates.longitude }
      : null);

  const onLoad = () => {
    if (coordinates) {
      setPhotoLocation({
        lat: coordinates.latitude,
        lng: coordinates.longitude,
      });
    }
  };

  const handleSave = () => {
    onSave?.({
      placeId: editPlaceId,
      userDescription: editUserDescription,
    });
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  return isLoaded ? (
    <Grid container spacing={1}>
      <Grid size={4}>
        <Label>{t("photos.photoDrawer.uploadedLabel")}</Label>
      </Grid>
      <Grid size={8}>
        <Value>{DateUtil.formatDateTime(createdDate)}</Value>
      </Grid>

      <Grid size={4}>
        <Label>{t("photos.photoDrawer.photoDateLabel")}</Label>
      </Grid>
      <Grid size={8}>
        <Value>{photoDate ? DateUtil.formatDateTime(photoDate) : ""}</Value>
      </Grid>

      <Grid size={4}>
        <Label>{t("photos.photoDrawer.location.label")}</Label>
      </Grid>
      <Grid size={8}>
        {isEditMode ? (
          <Box sx={{ "& #photo-place-autocomplete": { width: "100%" } }}>
            {editLocationDisplay && !isEditingLocation ? (
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Value>{editLocationDisplay}</Value>
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    setSavedLocationForCancel({
                      placeId: editPlaceId,
                      display: editLocationDisplay,
                    });
                    setEditPlaceId("");
                    setEditLocationDisplay("");
                    setSelectedPlaceLocation(null);
                    setIsEditingLocation(true);
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "primary.main",
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: "18px" }} />
                  {t("photos.photoDrawer.edit.changeLocation")}
                </Box>
              </Box>
            ) : (
              <>
                <div
                  ref={autocompleteContainerRef}
                  style={{ width: "100%", display: showLocationField ? "block" : "none" }}
                />
                {showLocationField && (
                  <Box display="flex" gap={1} marginTop="8px" flexWrap="wrap">
                    {isEditingLocation && (
                      <>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => {
                            setEditPlaceId("");
                            setEditLocationDisplay("");
                            setSelectedPlaceLocation(null);
                            setIsEditingLocation(false);
                          }}
                          sx={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "primary.main",
                          }}
                        >
                          {t("photos.photoDrawer.edit.clearLocation")}
                        </Box>
                        {savedLocationForCancel.display && (
                          <Box
                            component="button"
                            type="button"
                            onClick={() => {
                              setEditPlaceId(savedLocationForCancel.placeId);
                              setEditLocationDisplay(
                                savedLocationForCancel.display,
                              );
                              setSelectedPlaceLocation(null);
                              setIsEditingLocation(false);
                              setSavedLocationForCancel({
                                placeId: undefined,
                                display: "",
                              });
                            }}
                            sx={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                              fontSize: "14px",
                              color: "text.secondary",
                            }}
                          >
                            {t("photos.photoDrawer.edit.cancelLocation")}
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        ) : (
          <Value>
            {place?.name || t("photos.photoDrawer.location.unknown")}
          </Value>
        )}

        {coordinates && !isEditMode && (
          <Box marginTop="8px">
            <GoogleMap
              mapContainerStyle={{
                width: "100%",
                height: "200px",
              }}
              onLoad={onLoad}
              center={photoLocation}
              zoom={12}
            >
              {photoLocation && <Marker position={photoLocation} />}
            </GoogleMap>
          </Box>
        )}
        {isEditMode && editPlaceId && mapCenter && (
          <Box marginTop="8px">
            <GoogleMap
              mapContainerStyle={{
                width: "100%",
                height: "200px",
              }}
              onLoad={onLoad}
              center={mapCenter}
              zoom={12}
            >
              <Marker position={mapCenter} />
            </GoogleMap>
          </Box>
        )}
      </Grid>

      {isEditMode && (
        <Grid size={12}>
          <Label style={{ marginBottom: "4px", display: "block" }}>
            {t("photos.photoDrawer.edit.caregiverNote")}
          </Label>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            placeholder={t("photos.photoDrawer.edit.caregiverNotePlaceholder")}
            value={editUserDescription}
            onChange={(e) => setEditUserDescription(e.target.value)}
            margin="dense"
          />
        </Grid>
      )}
    </Grid>
  ) : (
    <></>
  );
  },
);

export default PhotoMetaData;
